import React from 'react';
import PropTypes from 'prop-types';
import Hotkeys from 'react-hot-keys';

import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import Slider from '@mui/material/Slider';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Fab from '@mui/material/Fab';
import CachedIcon from '@mui/icons-material/Cached';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { grey } from '@mui/material/colors';
import {styled} from '@mui/system';

import GlobalContext from '../helpers/context';
import {postCall} from '../helpers/request';
import {concatUrl} from '../helpers/url';
import {removeFromArray} from '../helpers/array';
import {autoPluralize} from '../helpers/pluralizer';
import GrantListItem from '../component/molecule/GrantListItem';
import PublicationListItem from '../component/molecule/PublicationListItem';
import ProgressSpinner from '../component/atoms/ProgressSpinner';
import FormButton from '../component/atoms/FormButton';
import HotKeyChip from '../component/atoms/HotKeyChip';
import {showActiveYears} from '../helpers/active_years';
import DocumentVerifiedStatus from '../component/molecule/DocumentVerifiedStatus';


const default_verified_filter = 'all';


const StyledErrorMessage = styled(Typography)(({ theme }) =>({
    color: theme.custom.color.error,
    fontWeight: theme.custom.font.bold,
    marginTop: 30,
    marginBottom: 30,
    textAlign: 'center'
}));

const StyledSplitter = styled('div')({
    marginBottom: 30
});

const StyledDocumentHeader = styled('div')(({ theme }) =>({
    display: 'flex',
    padding: '10px 30px 10px 14px',
    marginTop: 0,
    backgroundColor: theme.palette.secondary.main
}));

const StyledDocumentItem = styled('div')({
    display: 'flex',
    padding: '10px 62px 10px 24px'
});

const StyledDocumentItemDisabled = styled(StyledDocumentItem)({
    color: grey[500]
});

const StyledChangeStatus = styled('div')(({ theme }) =>({
    color: grey[900],
    fontWeight: theme.custom.font.bold
}));

const StyledActionButtons = styled('div')(({ theme }) =>({
    position: 'sticky',
    top: 0,
    zIndex: 100,
    padding: '10px 0 10px 0',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid',
    borderBottomColor: theme.palette.secondary.main
}));

const StyledDocumentSelection = styled('div')(({ theme }) =>({
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    lineHeight: theme.custom.font.line_height,
    flexBasis: '5%'
}));

const StyledDocumentSelectionCheckbox = styled(Checkbox)({
    padding: 0
});

const StyledDocumentTitle = styled('div')(({ theme }) =>({
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    lineHeight: theme.custom.font.line_height,
    flexBasis: '10%',
    textAlign: 'right',
    paddingRight: 30
}));

const StyledDocumentData = styled('div')(({ theme }) =>({
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    lineHeight: theme.custom.font.line_height,
    flexBasis: '80%'
}));

const StyledDocumentVerified = styled('div')(({ theme }) =>({
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    lineHeight: theme.custom.font.line_height,
    flexBasis: '5%',
    paddingTop: 1
}));

const StyledVerifiedFilter = styled(Select)(({ theme }) =>({
    marginLeft: 10,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize
}));

const StyledNoDocuments = styled('div')(({ theme }) =>({
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    marginTop: 30,
    marginBottom: 30,
    textAlign: 'center'
}));

const StyledFilterForm = styled('div')({
    marginBottom: 20
});

const StyledFormTitle = styled(Typography)({
    marginBottom: 20,
    fontWeight: 500,
    fontSize: 20
});

const StyledFormControl = styled(FormControl)({
    zIndex: 1
});

const StyledFormFab = styled(Fab)({
    height: '48px',
    width: '48px'
});

const StyledListTitle = styled(Typography)(({ theme }) =>({
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.custom.font.large,
    fontWeight: 500,
    lineHeight: theme.custom.font.line_height,
    marginTop: 20,
    marginBottom: 10
}));

const StyledHeaderPaging = styled('div')(({ theme }) =>({
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    lineHeight: theme.custom.font.line_height,
    flexBasis: '80%',
    paddingTop: 5
}));

const StyledDocumentHeaderText = styled('div')(({ theme }) =>({
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    lineHeight: theme.custom.font.line_height,
    flexBasis: '20%'
}));

const StyledHeaderTextResult = styled('div')(({ theme }) =>({
    color: theme.palette.secondary.contrastText,
    fontWeight: theme.custom.font.bold,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    lineHeight: theme.custom.font.line_height,
    flexBasis: '20%'
}));

const StyledSliderDefault = styled(Slider)({
    height: 0,
    padding: '40px 0 0 0',
    marginLeft: -20,
    '& .MuiSlider-track': {
        height: 8,
        borderRadius: 4
    },
    '& .MuiSlider-thumb': {
        height: 40,
        width: 40,
        backgroundColor: '#ffffff',
        border: '2px solid currentColor',
        marginTop: 0,
        marginLeft: 0,
        '&:focus, &:hover, &$active': {
            boxShadow: 'inherit'
        }
    },
    '& .MuiSlider-valueLabel': {
        left: 'calc(-50%+10px)',
        top: 31,
        backgroundColor: 'transparent',
        '& *': {
            color: '#000000',
            fontSize: '12px'
        }
    },
    '& .MuiSlider-rail': {
        height: 8,
        borderRadius: 4
    }
});

const StyledSliderDisabled = styled(StyledSliderDefault)({
      '& .MuiSlider-track': {
          color: '#e0e0e0'
      },
      '& .MuiSlider-thumb': {
          borderColor: '#e0e0e0'
      },
      '& .MuiSlider-valueLabel': {
          '& *': {
              color: '#e0e0e0'
          }
      },
        '& .MuiSlider-rail': {
          backgroundColor: '#e0e0e0'
      }
});


class CurationProfileSplit extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            curation_profile_id: this.props.curation_profile_id,
            workspace_id: this.props.workspace_id,
            publication_pages: undefined,
            current_publication_page: 1,
            grant_pages: undefined,
            current_grant_page: 1,
            publications_count: 0,
            grants_count: 0,
            documents: {
                publications: undefined,
                grants: undefined,
            },
            all_publications_selected: false,
            all_grants_selected: false,
            selected_ids: [],   // currently selected ids
            splitted_ids: [],   // ids of splitted items used to grey items as long the pages are not refreshed
            verified_ids: [],   // ids of items that was changed into verified
            unverified_ids: [], // ids of items that was changed into unverified
            form_errors: {},
            search_is_running: false,
            show_max_results: false,
            filter_affiliations: '',
            filter_affiliations_not: false,
            filter_authors_lastname: '',
            filter_authors_lastname_not: false,
            filter_authors_firstname: '',
            filter_authors_firstname_not: false,
            min_year: undefined,
            max_year: undefined,
            filter_year_range_active: true,
            filter_min_year: undefined,
            filter_max_year: undefined,
            filter_year_range_not: false,
            filter_verified: default_verified_filter,
        };

        this.is_mounted = false;
        this.load_curation_profile_documents_url = concatUrl(window.ROOT_URL, 'api', 'curation_profile', 'documents');
        this.load_curation_profile_documents_page_url = concatUrl(window.ROOT_URL, 'api', 'curation_profile', 'documents_page');
        this.split_curation_profile_url = concatUrl(window.ROOT_URL, 'api', 'curation_profile', 'split');
        this.verify_curation_profile_url = concatUrl(window.ROOT_URL, 'api', 'curation_profile', 'verify');

    }

    componentDidMount(){
        this.is_mounted = true;
        if (this.props.curation_profile_id){
            this.loadCurationProfileDocuments();
        }
    }

    componentWillUnmount(){
        this.is_mounted = false;
    }

    stopPropagation = (event) => {
        event.stopPropagation();
    };


    // +++ load documents +++++++++++++++++++++++++++++++++++++++++++++++

    loadCurationProfileDocumentsFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.setState({
            search_is_running: false,
            documents: {
                publications: undefined,
                grants: undefined,
            },
        });
        this.context.displayGlobalError('Backend service failure: Could not load profile documents.');
    };

    loadCurationProfileDocumentsDone = (data) => {
        if(!this.is_mounted){
            // skip updating component if not mounted to avoid.
            return;
        }

        this.setState({
            publications_count: data.publications_count,
            grants_count: data.grants_count,
            publication_pages: data.publication_pages,
            grant_pages: data.grant_pages,
            documents: data.documents,
            search_is_running: false,
        });

        if (!(this.state.filter_min_year || this.state.filter_max_year)){
            this.setState({
                filter_min_year: data.min_year,
                filter_max_year: data.max_year
            });
        }

        if (!(this.state.min_year || this.state.max_year)){
            this.setState({
                min_year: data.min_year,
                max_year: data.max_year
            });
        }

    };

    loadCurationProfileDocuments = () => {

        this.setState({
            search_is_running: true,
            documents: {
                publications: undefined,
                grants: undefined,
            },
            publication_pages: undefined,
            grant_pages: undefined,
            selected_ids: [],
            splitted_ids: [],
            verified_ids: [],
            unverified_ids: [],
        });

        let data = {
            id: this.props.curation_profile_id,
            workspace_id: this.props.workspace_id,
            filter_affiliations: this.state.filter_affiliations,
            filter_affiliations_not: this.state.filter_affiliations_not,
            filter_authors_lastname: this.state.filter_authors_lastname,
            filter_authors_lastname_not: this.state.filter_authors_lastname_not,
            filter_authors_firstname: this.state.filter_authors_firstname,
            filter_authors_firstname_not: this.state.filter_authors_firstname_not,
            filter_year_range_active: this.state.filter_year_range_active,
            filter_min_year: this.state.filter_min_year,
            filter_max_year: this.state.filter_max_year,
            filter_year_range_not: this.state.filter_year_range_not,
            filter_verified: this.state.filter_verified,
            show_max_results: this.state.show_max_results
        } ;

        postCall(this.load_curation_profile_documents_url,
            data,
            this.loadCurationProfileDocumentsDone,
            this.loadCurationProfileDocumentsFailed);
    };


    // +++ split out selected documents +++++++++++++++++++++++++++++++++++++++++++++++

    splitOutSelectionFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }
        this.context.displayGlobalError('Backend service failure: Could not split out the selected documents.');
    };

    splitOutSelectionDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }
        if (data && data.form_error){
            this.setState({
                form_errors: {split: data.form_error}
            });
        }else {
            if (data) {
                this.manageSplittedDocumentsStatus();
                this.props.splitCallback(this.props.curation_profile_id);
                this.context.displayGlobalInfo('Split was successfully.');
            } else {
                callback_failure();
            }
        }
    };

    splitOutSelection = () => {
        this.setState({
            form_errors: {}
        });
        if(!this.state.selected_ids || this.state.selected_ids.length === 0) {
            throw new Error('No IDs selected!');
        }

        const data = {
            workspace_id: this.state.workspace_id,
            split_ids: this.state.selected_ids,
            curation_profile_id: this.props.curation_profile_id
        };
        postCall(this.split_curation_profile_url, data, this.splitOutSelectionDone, this.splitOutSelectionFailed);
    };

    manageSplittedDocumentsStatus = () => {
        // manage the status of splitted documents in the list as long the pages are not refreshed.
        let selected_ids = this.state.selected_ids;
        let splitted_ids = this.state.splitted_ids;
        this.setState({
            splitted_ids: splitted_ids.concat(selected_ids),
            selected_ids: [],
            all_publications_selected: false,
            all_grants_selected: false,
        });
    };


    // +++ paging +++++++++++++++++++++++++++++++++++++++++++++++

    loadPageFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }
        this.context.displayGlobalError('Backend service failure: Could not load selected page.');
    };

    loadPublicationsDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }
        if (data) {
            let documents = this.state.documents;
            documents['publications'] = data.documents;
            this.setState({
                current_publication_page: data.page,
                documents: documents,
                all_publications_selected: false,
                all_grants_selected: false
            });
        } else {
            callback_failure();
        }
    };

    loadPublicationsPage = (event, page) => {
        const data = {
            workspace_id: this.state.workspace_id,
            curation_profile_id: this.props.curation_profile_id,
            source: 'publications',
            page: page,
            show_max_results: this.state.show_max_results
        };
        postCall(this.load_curation_profile_documents_page_url, data, this.loadPublicationsDone, this.loadPageFailed);
    };

    loadGrantsDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }
        if (data) {
            let documents = this.state.documents;
            documents['grants'] = data.documents;
            this.setState({
                current_grant_page: data.page,
                documents: documents,
                all_publications_selected: false,
                all_grants_selected: false
            });
        } else {
            callback_failure();
        }
    };

    loadGrantsPage = (event, page) => {
        const data = {
            workspace_id: this.state.workspace_id,
            curation_profile_id: this.props.curation_profile_id,
            source: 'grants',
            page: page,
            show_max_results: this.state.show_max_results
        };
        postCall(this.load_curation_profile_documents_page_url, data, this.loadGrantsDone, this.loadPageFailed);
    };


    // +++ mark/unmark selected documents as verified +++++++++++++++++++++++++++++++++++++++++++++++

    changeVerifySelectionFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }
        this.context.displayGlobalError('Backend service failure: Could not change verification status of selected documents.');
    };

    changeVerifySelectionDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }
        if (data && data.form_error){
            this.setState({
                form_errors: {verify: data.form_error}
            });
        }else {
            if (data) {
                if(data.verified){
                    this.context.displayGlobalInfo('Documents marked as verified.');
                }else{
                    this.context.displayGlobalInfo('Documents marked as not verified.');
                }
                this.manageVerifiedDocumentsStatus(data.verified);
                this.props.splitCallback(this.props.curation_profile_id);
            } else {
                callback_failure();
            }
        }
    };

    verifySelection = () => {
        this.setState({
            form_errors: {}
        });
        if(!this.state.selected_ids || this.state.selected_ids.length === 0) {
            throw new Error('No IDs selected!');
        }

        const data = {
            workspace_id: this.state.workspace_id,
            selected_ids: this.state.selected_ids,
            curation_profile_id: this.props.curation_profile_id,
            verify: true
        };
        postCall(this.verify_curation_profile_url, data, this.changeVerifySelectionDone, this.changeVerifySelectionFailed);
    };

    unverifySelection = () => {
        this.setState({
            form_errors: {}
        });
        if(!this.state.selected_ids || this.state.selected_ids.length === 0) {
            throw new Error('No IDs selected!');
        }

        const data = {
            workspace_id: this.state.workspace_id,
            selected_ids: this.state.selected_ids,
            curation_profile_id: this.props.curation_profile_id,
            verify: false
        };
        postCall(this.verify_curation_profile_url, data, this.changeVerifySelectionDone, this.changeVerifySelectionFailed);
    };

    manageVerifiedDocumentsStatus = (verified) => {
        // manage verified/unverified documents status in the list as long the pages are not refreshed.
        let selected_ids = this.state.selected_ids;
        let verified_ids = this.state.verified_ids;
        let unverified_ids = this.state.unverified_ids;

        if (verified){
            // add new verified items
            verified_ids = verified_ids.concat(selected_ids);
            // clean up not verified items
            unverified_ids = unverified_ids.filter(x => !verified_ids.includes(x));
        }else{
             // add new unverified items
            unverified_ids = unverified_ids.concat(selected_ids);
            // clean up verified items
            verified_ids = verified_ids.filter(x => !unverified_ids.includes(x));
        }

        this.setState({
            verified_ids: verified_ids,
            unverified_ids: unverified_ids,
            selected_ids: [],
            all_publications_selected: false,
            all_grants_selected: false,
        });
    };

    manageSingleSelection = (checked, document, selected_ids) => {
        if(checked){
            // set the general "is-selected" status
            if(!this.isSelected(document.po_id)){
                selected_ids.push(document.po_id);
            }
        }else{
            selected_ids = removeFromArray(selected_ids, document.po_id);
        }
        return selected_ids;
    };

    updateSelection = (document, checked) => {
        let selected_ids = this.manageSingleSelection(checked, document, this.state.selected_ids);
        this.setState({
            selected_ids: selected_ids
        });
    };

    isSelected = po_id => {
        return this.state.selected_ids.indexOf(po_id) > -1;
    };

    documentSelection = (document, disabled) => {
        return (
            <StyledDocumentSelectionCheckbox
                checked={this.isSelected(document.po_id)}
                onChange={(event) => {this.updateSelection(document, event.currentTarget.checked)}}
                value={document.po_id}
                disabled={disabled}
            />
        );
    };

    selectAll = (source_key) => {
        return (
            <span>
                <Checkbox
                    checked={this.state['all_' + source_key + '_selected']}
                    onChange={() => {this.switchSelection(source_key)}}
                    value={true} color="primary"
                />
                <span>select all</span>
            </span>
        );
    };

    switchSelection = (source_key) => {
        let doc_type_status_key = 'all_' + source_key + '_selected';
        let selected_ids = this.state.selected_ids;

        if (this.state.documents[source_key]){
            this.state.documents[source_key].map((document) => {
                if(this.state.splitted_ids.indexOf(document.po_id) === -1){
                    selected_ids = this.manageSingleSelection(this.state[doc_type_status_key] === false, document, selected_ids);
                }
            });
        }

        let status_dict = {
            selected_ids: selected_ids,
        };
        status_dict[doc_type_status_key] = !this.state[doc_type_status_key];

        this.setState(status_dict);
    };

    verifiedInfo = (document) => {
        let verified = document.verified;
        if(this.state.verified_ids.indexOf(document.po_id) >= 0){
            verified = true;
        }
        if(this.state.unverified_ids.indexOf(document.po_id) >= 0){
            verified = false;
        }
        return (
            <DocumentVerifiedStatus verified={verified} />
        );
    };

    publicationList = () => {
        if(this.state.documents.publications === undefined){
            return <ProgressSpinner />;
        }
        if(this.state.documents.publications.length > 0){
            return (
                this.state.documents.publications.map((publication) => {
                    let split_out = this.state.splitted_ids.indexOf(publication.po_id) >= 0;
                    let changed_verified_status = false;
                    let changed_verified_status_label = '';
                    if (this.state.filter_verified !== default_verified_filter){
                        if(this.state.verified_ids.indexOf(publication.po_id) >= 0){
                            changed_verified_status_label = 'verified';
                            changed_verified_status = true;
                        }
                        if(this.state.unverified_ids.indexOf(publication.po_id) >= 0){
                            changed_verified_status_label = 'unverified';
                            changed_verified_status = true;
                        }
                    }
                    let disabled = split_out || changed_verified_status;
                    let DocumentItme = disabled ? StyledDocumentItemDisabled : StyledDocumentItem;
                    return (
                        <DocumentItme key={publication.id}>
                            <StyledDocumentSelection>
                                {this.documentSelection(publication, disabled)}
                            </StyledDocumentSelection>
                            <StyledDocumentVerified>
                            {this.verifiedInfo(publication)}
                            </StyledDocumentVerified>
                            <StyledDocumentTitle>
                                {publication.pub_year}
                                {split_out && <StyledChangeStatus>split out</StyledChangeStatus>}
                                {changed_verified_status && <StyledChangeStatus>{changed_verified_status_label}</StyledChangeStatus>}
                            </StyledDocumentTitle>
                            <StyledDocumentData>
                                <PublicationListItem key={publication.id} publication={publication}
                                                     hideYears={true} disabled={disabled}/>
                            </StyledDocumentData>
                        </DocumentItme>
                    );
                })
            );
        }
        return (
            <StyledNoDocuments>
                No publications available
            </StyledNoDocuments>
        );
    };

    grantList = () => {
        if(this.state.documents.grants === undefined){
            return <ProgressSpinner />;
        }
        if(this.state.documents.grants.length > 0){
            return (
                this.state.documents.grants.map((grant) => {
                    let split_out = this.state.splitted_ids.indexOf(grant.po_id) >= 0;
                    let changed_verified_status = false;
                    let changed_verified_status_label = '';
                    if (this.state.filter_verified !== default_verified_filter){
                        if(this.state.verified_ids.indexOf(grant.po_id) >= 0){
                            changed_verified_status_label = 'verified';
                            changed_verified_status = true;
                        }
                        if(this.state.unverified_ids.indexOf(grant.po_id) >= 0){
                            changed_verified_status_label = 'unverified';
                            changed_verified_status = true;
                        }
                    }
                    let disabled = split_out || changed_verified_status;
                    let DocumentItme = disabled ? StyledDocumentItemDisabled : StyledDocumentItem;
                    return (
                        <DocumentItme key={grant.id} >
                            <StyledDocumentSelection>
                                {this.documentSelection(grant, disabled)}
                            </StyledDocumentSelection>
                            <StyledDocumentVerified>
                                {this.verifiedInfo(grant)}
                            </StyledDocumentVerified>
                            <StyledDocumentTitle>
                                {showActiveYears(grant.active_year)}
                                {split_out && <StyledChangeStatus>split out</StyledChangeStatus>}
                                {changed_verified_status && <StyledChangeStatus>{changed_verified_status_label}</StyledChangeStatus>}
                            </StyledDocumentTitle>
                            <StyledDocumentData>
                                <GrantListItem key={grant.id} grant={grant} hideYears={true} disabled={disabled}/>
                            </StyledDocumentData>
                        </DocumentItme>
                    );
                })
            );
        }
        return (
            <StyledNoDocuments>
                No grants available
            </StyledNoDocuments>
        );
    };

    documentsListHeader = (label, count, source_key, paging) => {
        return (
            <div>
                <StyledListTitle>{autoPluralize(count, label)}</StyledListTitle>
                <StyledDocumentHeader>
                    <StyledHeaderTextResult>
                        {this.selectAll(source_key)}
                    </StyledHeaderTextResult>
                    <StyledHeaderPaging>
                        {paging}
                    </StyledHeaderPaging>
                    <StyledDocumentHeaderText></StyledDocumentHeaderText>
                </StyledDocumentHeader>
            </div>
        );
    };

    paging = (pages, current_page, callback) => {
        if (pages){
            return (
                 <Grid container justifyContent="center">
                     <Grid>
                         <Pagination count={pages} page={current_page} onChange={callback}
                                     siblingCount={5} color="primary"
                                     showFirstButton={true} showLastButton={true}/>
                     </Grid>
                 </Grid>
            );
        }
    };

    showSplitConfirmationDialog = (event) => {
        this.stopPropagation(event);

        this.context.showConfirmationDialog({
            confirm_dialog_on_confirm: this.splitOutSelection,
            confirm_dialog_mode: null,
            confirm_dialog_title: 'Please confirm',
            confirm_dialog_text: 'Are you sure you want to split the ' + this.state.selected_ids.length + ' selected document(s) from this curation profile?',
            confirm_dialog_button_text: 'split'
        });
    };

    showVerifyConfirmationDialog = (event) => {
        this.stopPropagation(event);

        this.context.showConfirmationDialog({
            confirm_dialog_on_confirm: this.verifySelection,
            confirm_dialog_mode: null,
            confirm_dialog_title: 'Please confirm',
            confirm_dialog_text: 'Are you sure you want to mark the ' + this.state.selected_ids.length + ' selected document(s) as verified?',
            confirm_dialog_button_text: 'mark as verified'
        });
    };

    showNotVerifyConfirmationDialog = (event) => {
        this.stopPropagation(event);

        this.context.showConfirmationDialog({
            confirm_dialog_on_confirm: this.unverifySelection,
            confirm_dialog_mode: null,
            confirm_dialog_title: 'Please confirm',
            confirm_dialog_text: 'Are you sure you want to mark the ' + this.state.selected_ids.length + ' selected document(s) as not verified?',
            confirm_dialog_button_text: 'mark as not verified'
        });
    };

    showErrorMessage = (key) => {
        if (key in this.state.form_errors && this.state.form_errors[key]) {
            return (
                <StyledErrorMessage>
                    {this.state.form_errors[key]}
                </StyledErrorMessage>
            );
        }
    };

    verifiedFilter = () => {

        const verifiedOptions = [
            {key: true, label: 'verified only'},
            {key: false, label: 'not verified only'},
        ];

        return (
            <FormControl variant="standard">
                <StyledVerifiedFilter
                    id="verifiedFilter"
                    value={this.state.filter_verified}
                    autoWidth
                    onChange={(event) => {
                        this.setState(
                            {filter_verified: event.target.value},
                            () => {
                                this.loadCurationProfileDocuments();
                            }
                        );
                    }}
                >
                    <MenuItem value={default_verified_filter} key="default">
                        verified & not verified
                    </MenuItem>
                    {verifiedOptions.map((status_option) =>
                        <MenuItem value={status_option.key} key={status_option.key}>
                            {status_option.label}
                        </MenuItem>)}
                </StyledVerifiedFilter>
            </FormControl>
        );
    };

    actionButtons = () => {
        let disable_button = !this.state.selected_ids || this.state.selected_ids.length === 0;
        return (
            <StyledActionButtons>
                <Grid container spacing={5}>
                    <Grid>
                        <FormButton text="Split" disabled={disable_button}
                                      actionCallback={this.showSplitConfirmationDialog} />
                    </Grid>
                    <Grid>
                        show: {this.verifiedFilter()}
                    </Grid>
                    <Grid>
                        <FormButton text="Mark as verified" disabled={disable_button}
                                      actionCallback={this.showVerifyConfirmationDialog} />
                        <FormButton text="Mark as not verified" disabled={disable_button}
                                      actionCallback={this.showNotVerifyConfirmationDialog} />
                    </Grid>
                    <Grid>
                        <Checkbox
                            checked={this.state['show_max_results']}
                            onChange={() => {
                                this.setState({show_max_results: !this.state.show_max_results});
                            }}
                            value={true} color="primary"
                        />
                        <span>Show large result list</span>
                    </Grid>
                </Grid>
            </StyledActionButtons>
        );
    };


    filterForm = () => {
        let track_mode = this.state.filter_year_range_not ? 'inverted' : 'normal';
        let StyledSlider = this.state.filter_year_range_active ? StyledSliderDefault : StyledSliderDisabled;
        return (
            <StyledFilterForm>
                <StyledFormTitle>
                    Filter documents
                </StyledFormTitle>
                <form>
                    <Grid container spacing={2}>
                        <Grid size={3}>
                            <StyledFormControl fullWidth={true}>
                                <TextField
                                    variant="outlined"
                                    id="filter_affiliations"
                                    multiline
                                    minRows="3"
                                    maxRows="10"
                                    label="Affiliation"
                                    placeholder="e.g. Institute of Tech*"
                                    helperText="Use new lines to add multiple filters"
                                    value={this.state.filter_affiliations}
                                    onChange={(event) => {
                                        this.setState({filter_affiliations: event.target.value});
                                    }}
                                />
                            </StyledFormControl>
                        </Grid>
                        <Grid size={1}>
                            <StyledFormControl fullWidth={true}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={this.state.filter_affiliations_not}
                                            onChange={() => {
                                                this.setState({filter_affiliations_not: !this.state.filter_affiliations_not});
                                            }}
                                            name="filter_affiliations_not"
                                            color="primary"
                                        />
                                    }
                                    label="NOT"
                                />
                            </StyledFormControl>
                        </Grid>
                        <Grid size={2}>
                            <StyledFormControl fullWidth={true}>
                                <TextField
                                    variant="outlined"
                                    id="filter_authors_lastname"
                                    multiline
                                    minRows="3"
                                    maxRows="10"
                                    label="Author last name"
                                    placeholder="e.g. Williams or Phil*"
                                    helperText="Use new lines to add multiple filters"
                                    value={this.state.filter_authors_lastname}
                                    onChange={(event) => {
                                        this.setState({filter_authors_lastname: event.target.value});
                                    }}
                                />
                            </StyledFormControl>
                        </Grid>
                        <Grid size={1}>
                            <StyledFormControl fullWidth={true}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={this.state.filter_authors_lastname_not}
                                            onChange={() => {
                                                this.setState({filter_authors_lastname_not: !this.state.filter_authors_lastname_not});
                                            }}
                                            name="filter_authors_lastname_not"
                                            color="primary"
                                        />
                                    }
                                    label="NOT"
                                />
                            </StyledFormControl>
                        </Grid>
                        <Grid size={2}>
                            <StyledFormControl fullWidth={true}>
                                <TextField
                                    variant="outlined"
                                    id="filter_authors_firstname"
                                    multiline
                                    minRows="3"
                                    maxRows="10"
                                    label="Author first name"
                                    placeholder="e.g. Paul or J*"
                                    helperText="Use new lines to add multiple filters"
                                    value={this.state.filter_authors_firstname}
                                    onChange={(event) => {
                                        this.setState({filter_authors_firstname: event.target.value});
                                    }}
                                />
                            </StyledFormControl>
                        </Grid>
                        <Grid size={1}>
                            <StyledFormControl fullWidth={true}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={this.state.filter_authors_firstname_not}
                                            onChange={() => {
                                                this.setState({filter_authors_firstname_not: !this.state.filter_authors_firstname_not});
                                            }}
                                            name="filter_authors_firstname_not"
                                            color="primary"
                                        />
                                    }
                                    label="NOT"
                                />
                            </StyledFormControl>
                        </Grid>
                        <Grid size={2}>
                            <StyledFormFab aria-label="add" color="primary"
                                           disabled={this.state.search_is_running} onClick={this.loadCurationProfileDocuments}>
                                    <CachedIcon />
                            </StyledFormFab>
                            <HotKeyChip hotKey="alt+f" />
                            <Hotkeys keyName="alt+f" onKeyDown={this.loadCurationProfileDocuments} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid size={3}>
                            <StyledFormControl fullWidth={true}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={this.state.filter_year_range_active}
                                            onChange={() => {
                                                this.setState({filter_year_range_active: !this.state.filter_year_range_active});
                                            }}
                                            name="filter_year_range_active"
                                            color="primary"
                                        />
                                    }
                                    label="Include year range filter"
                                />
                            </StyledFormControl>
                        </Grid>
                        <Grid size={6}>
                             <StyledFormControl fullWidth={true}>
                                {this.state.min_year && this.state.max_year && <StyledSlider
                                    track={track_mode}
                                    aria-labelledby="year-range-slider"
                                    min={this.state.min_year}
                                    max={this.state.max_year}
                                    defaultValue={[this.state.min_year, this.state.max_year]}
                                    valueLabelDisplay="on"
                                    onChange={(_, value) => {
                                        this.setState({
                                            filter_min_year: value[0],
                                            filter_max_year: value[1]
                                        });
                                    }}
                                />}
                            </StyledFormControl>
                        </Grid>
                        <Grid size={1}>
                            <StyledFormControl fullWidth={true}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={this.state.filter_year_range_not}
                                            onChange={() => {
                                                this.setState({filter_year_range_not: !this.state.filter_year_range_not});
                                            }}
                                            name="filter_year_range_not"
                                            color="primary"
                                        />
                                    }
                                    label="NOT"
                                />
                            </StyledFormControl>
                        </Grid>
                    </Grid>
                </form>
            </StyledFilterForm>
        );
    };

    render() {
        return (
            <GlobalContext.Consumer>{() => (
                <StyledSplitter>

                    {this.filterForm()}

                    {this.showErrorMessage('split')}
                    {this.showErrorMessage('verify')}

                    {this.actionButtons()}

                    {this.documentsListHeader('Publication', this.state.publications_count, 'publications',
                        this.paging(this.state.publication_pages, this.state.current_publication_page, this.loadPublicationsPage))}
                    {this.publicationList()}

                    {this.documentsListHeader('Grant', this.state.grants_count, 'grants',
                        this.paging(this.state.grant_pages, this.state.current_grant_page, this.loadGrantsPage))}
                    {this.grantList()}

                </StyledSplitter>
            )}
            </GlobalContext.Consumer>
        );
    }
}

CurationProfileSplit.contextType = GlobalContext;

CurationProfileSplit.propTypes = {
    curation_profile_id: PropTypes.number.isRequired,
    workspace_id: PropTypes.number.isRequired,
    splitCallback: PropTypes.func
};

export default CurationProfileSplit;
